// Shared list controls: a labelled search box, filter chips that wrap (never
// scroll sideways, so every option stays visible), and changing many rows at once.
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useId, useState } from "react";
import { Button, ChoiceChips, fieldClass, onNavyClass } from "./ui.tsx";

export function SearchBox({
	value,
	onChange,
	placeholder,
}: {
	value: string;
	onChange: (v: string) => void;
	placeholder: string;
}) {
	const id = useId();
	return (
		<div className="w-full sm:max-w-md">
			<label htmlFor={id} className="block font-bold">
				Search
			</label>
			<div className="relative mt-2">
				<MagnifyingGlass
					size={20}
					aria-hidden="true"
					className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-soft"
				/>
				<input
					id={id}
					type="search"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className={`${fieldClass} pl-10`}
				/>
			</div>
		</div>
	);
}

/** Filters above a list: one choice, as pills. Labelled "Show" for screen readers unless a question is given. */
export function FilterChips({
	name,
	options,
	value,
	onChange,
	legend,
}: {
	name: string;
	options: readonly { value: string; label: string }[];
	value: string;
	onChange: (v: string) => void;
	legend?: string;
}) {
	return (
		<div className="mt-4">
			<ChoiceChips
				name={name}
				options={options}
				value={value}
				onChange={onChange}
				legend={legend ?? "Show"}
				legendHidden={!legend}
			/>
		</div>
	);
}

export function SkeletonCards() {
	return (
		<ul aria-hidden="true" className="mt-3 space-y-3">
			{[0, 1, 2].map((i) => (
				<li
					key={i}
					className="h-[104px] animate-pulse rounded-[var(--radius-card)] bg-card motion-reduce:animate-none"
				/>
			))}
		</ul>
	);
}

/** "Whose?" above a list: everyone's, mine, or one colleague's. */
export function ConsultantFilter({
	value,
	onChange,
	people,
	meId,
	noun,
}: {
	value: string;
	onChange: (v: string) => void;
	people: { id: string; name: string }[];
	meId: string | undefined;
	/** For example "candidates" or "clients". */
	noun: string;
}) {
	const id = useId();
	return (
		<div className="mt-4">
			<label htmlFor={id} className="sr-only">
				Consultant
			</label>
			<select
				id={id}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className={`${fieldClass} font-semibold`}
			>
				<option value="">Everyone's {noun}</option>
				<option value="me">My {noun}</option>
				{people
					.filter((p) => p.id !== meId)
					.map((p) => (
						<option key={p.id} value={p.id}>
							{p.name}'s {noun}
						</option>
					))}
			</select>
		</div>
	);
}

/** Ticked rows in a list, for changing many at once. */
export function useTicks() {
	const [ticked, setTicked] = useState<Set<string>>(new Set());
	return {
		ticked,
		toggle: (id: string) =>
			setTicked((t) => {
				const next = new Set(t);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			}),
		set: (ids: string[]) => setTicked(new Set(ids)),
		clear: () => setTicked(new Set()),
	};
}

/** One change the bulk bar can make: choose a value, then press the button. */
export interface BulkAction {
	key: string;
	label: string;
	options: { value: string; label: string }[];
	button: string;
	onRun: (value: string) => void;
}

/**
 * Shown while rows are ticked: change them all in one go. It sticks to the top
 * as the list scrolls; on phones each change sits on its own line.
 */
export function BulkBar({
	label,
	count,
	actions,
	busy,
	message,
	onClear,
}: {
	label: string;
	count: number;
	actions: BulkAction[];
	busy: boolean;
	message: { tone: "clear" | "stop"; text: string } | null;
	onClear: () => void;
}) {
	const [chosen, setChosen] = useState<Record<string, string>>({});
	const id = useId();
	return (
		<section
			aria-label={label}
			className="sticky top-0 z-10 mt-3 flex flex-col gap-3 rounded-[var(--radius-card)] bg-navy px-5 py-3 text-white md:flex-row md:flex-wrap md:items-center md:gap-x-6"
		>
			<p className="font-display font-bold">{count} ticked</p>
			{actions.map((a) => (
				<div key={a.key} className="flex flex-wrap items-center gap-2">
					<label htmlFor={`${id}-${a.key}`} className="w-full text-sm font-semibold sm:w-auto">
						{a.label}
					</label>
					<select
						id={`${id}-${a.key}`}
						value={chosen[a.key] ?? ""}
						onChange={(e) => setChosen((c) => ({ ...c, [a.key]: e.target.value }))}
						className={`${fieldClass} w-auto min-w-0 flex-1 sm:flex-none`}
					>
						<option value="">Choose</option>
						{a.options.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
					<button
						type="button"
						disabled={!chosen[a.key] || busy}
						onClick={() => a.onRun(chosen[a.key] as string)}
						className={onNavyClass}
					>
						{a.button}
					</button>
				</div>
			))}
			<button
				type="button"
				onClick={onClear}
				className="min-h-11 self-start font-semibold underline underline-offset-4 md:ml-auto md:self-auto"
			>
				Untick all
			</button>
			<div aria-live="polite" className="w-full empty:hidden">
				{message ? (
					<p className={`text-sm font-semibold ${message.tone === "stop" ? "text-pink" : "text-mint"}`}>
						{message.text}
					</p>
				) : null}
			</div>
		</section>
	);
}

/**
 * Phones: tick boxes stay out of the way until someone asks to change several.
 * Laptops always have them in the table.
 */
export function ChooseSeveral({
	on,
	onChange,
	allTicked,
	onTickAll,
}: {
	on: boolean;
	onChange: (on: boolean) => void;
	allTicked: boolean;
	onTickAll: () => void;
}) {
	return (
		<div className="mt-3 flex flex-wrap items-center gap-x-4 md:hidden">
			<Button variant="secondary" onClick={() => onChange(!on)} aria-pressed={on}>
				{on ? "Done choosing" : "Change several"}
			</Button>
			{on ? (
				<label className="flex min-h-12 cursor-pointer items-center gap-3 font-semibold">
					<input type="checkbox" className="kit-check" checked={allTicked} onChange={onTickAll} />
					Tick everyone shown
				</label>
			) : null}
		</div>
	);
}

/** A tick box in the first cell of a table row: the whole 48px cell is the target. */
export function TableTick({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
	return (
		<label className="flex min-h-12 w-12 cursor-pointer items-center justify-center">
			<input type="checkbox" className="kit-check" checked={checked} onChange={onChange} aria-label={label} />
		</label>
	);
}

/** A tick box at the start of a phone card, with a full-height target. */
export function CardTick({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
	return (
		<label className="flex min-w-12 shrink-0 cursor-pointer items-center justify-center self-stretch pl-3">
			<input type="checkbox" className="kit-check" checked={checked} onChange={onChange} aria-label={label} />
		</label>
	);
}
